import {
  createContext, ReactElement, ReactNode, useCallback, useContext, useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '~/contexts/newToastContext';
import { HealthVariant } from '../Health/components/UnitsInfo/types';
import { isOfflineRuleActive, parseHealthData, parseUnits } from '../helpers/health';
import { GetOverviewUnitHealth } from '../types';
import { getOverviewUnitHealth } from '../helpers/overview';
import { FilterContextType } from './FilterContext';

export interface HealthCountInfo {
  healthy: number,
  alert: number,
  critical: number,
  inoperative: number,
}

export type HealthUnitsInfo = {
  id: number;
  name: string;
  lat: number | null;
  lon: number | null;
  state: string;
  city: string;
  cityId: string;
  health: HealthVariant;
  machines: HealthCountInfo;
}[]

export interface HealthDataContextType {
  data: HealthCountInfo | undefined;
  units: HealthUnitsInfo;
  /** Indica que a API já aplica a regra de offline por conectividade nos contadores. */
  offlineRuleActive: boolean;
  loading: boolean;
  refetch: (clients: number[], selectedOptions: FilterContextType['selectedOptions']) => Promise<void>;
}

const HealthDataContext = createContext<HealthDataContextType | undefined>(undefined);

export const HealthDataProvider = ({
  children,
}: Readonly<{ children: ReactNode }>): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState<GetOverviewUnitHealth | undefined>();
  const { toast } = useToast();
  const { t } = useTranslation();

  const getHealthInfo = useCallback(async (
    clients: number[],
    selectedOptions: FilterContextType['selectedOptions'],
  ): Promise<void> => {
    setLoading(true);

    const data = await getOverviewUnitHealth(clients, {
      stateIds: selectedOptions.state,
      unitIds: selectedOptions.units?.map((item) => Number(item.value)) ?? selectedOptions.group?.units,
      healthStatus: selectedOptions.healthStatus,
      thermalComfortStatus: selectedOptions.status,
      cityIds: selectedOptions.cities?.map((item) => item.value),
    });

    if (data) {
      setResponse(data);
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosSaudeUnidades'), 'error');
    }

    setLoading(false);
  }, []);

  const offlineRuleActive = useMemo(() => (response ? isOfflineRuleActive(response) : false), [response]);
  const data = useMemo(() => (response ? parseHealthData(response) : undefined), [response]);
  const units = useMemo(
    () => (response ? parseUnits(response.UNITS, offlineRuleActive) : []),
    [offlineRuleActive, response],
  );

  const value = useMemo((): HealthDataContextType => ({
    data,
    loading,
    units,
    offlineRuleActive,
    refetch: getHealthInfo,
  }), [data, getHealthInfo, loading, offlineRuleActive, units]);

  return (
    <HealthDataContext.Provider value={value}>
      {children}
    </HealthDataContext.Provider>
  );
};

export const useHealthDataContext = (): HealthDataContextType => {
  const context = useContext(HealthDataContext);

  if (!context) {
    throw new Error('useHealthDataContext must be used within a HealthDataProvider.');
  }

  return context;
};
